import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
// Icons would be imported here if needed

export const AgentManuscriptReaderPage = () => {
  const manuscriptId = window.location.pathname.match(/\/agent\/manuscripts\/(\d+)\/read/)?.[1];

  const [currentChapter, setCurrentChapter] = useState(1);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [showDecision, setShowDecision] = useState(false);

  // Evaluation form state
  const [overallRating, setOverallRating] = useState<number>(0);
  const [premiseRating, setPremiseRating] = useState<number>(0);
  const [writingRating, setWritingRating] = useState<number>(0);
  const [charactersRating, setCharactersRating] = useState<number>(0);
  const [paceRating, setPaceRating] = useState<number>(0);
  const [marketabilityRating, setMarketabilityRating] = useState<number>(0);

  const [privateNotes, setPrivateNotes] = useState('');
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [editorialNotes, setEditorialNotes] = useState('');
  const [marketComps, setMarketComps] = useState('');
  const [targetAudience, setTargetAudience] = useState('');

  // Decision state
  const [decision, setDecision] = useState<'offer' | 'revise_resubmit' | 'pass' | ''>('');
  const [feedbackDraft, setFeedbackDraft] = useState('');

  // Mock data
  const manuscript = {
    id: manuscriptId,
    title: 'The Silent Shore',
    author: 'Emma Rodriguez',
    genre: 'Literary Fiction',
    wordCount: 85000,
    requestType: 'full',
    queryDate: '2024-02-15',
    receivedDate: '2024-02-18',
  };

  const chapters = [
    { number: 1, title: 'The Beginning', wordCount: 3500 },
    { number: 2, title: 'Discoveries', wordCount: 4200 },
    { number: 3, title: 'The Journey', wordCount: 3800 },
    { number: 4, title: 'Revelations', wordCount: 4000 },
    { number: 5, title: 'The Conclusion', wordCount: 3600 },
  ];

  const sampleContent = `The lighthouse stood silent against the storm, its beam cutting through sheets of rain like a blade through silk. Elena pressed her palm against the cold glass, watching the waves crash against the rocks below. She had come here seeking answers, but all she found were more questions.

Three months ago, her sister had disappeared from this very spot. The official report said it was an accident—a tragic fall during a stormy night. But Elena knew better. Her sister had been afraid of heights, would never have ventured so close to the edge.

"You shouldn't be here," a voice said behind her.

Elena turned to find an old man in a weathered coat, his face lined with years of sea and sun. His eyes held a warning.

"I'm looking for the truth about my sister," Elena said, her voice steady despite the fear coursing through her veins.

The old man's expression softened, but the warning remained. "Some truths are better left buried, miss. The sea keeps her secrets for a reason."

"What do you know about what happened to her?"

He was silent for a long moment, the only sound the howling wind and crashing waves. Finally, he spoke. "Your sister was asking questions. Questions about the old families, about the things that happened here fifty years ago. About the night the previous lighthouse keeper vanished."

Elena felt her heart race. This was the first real lead she'd had in months. "Tell me everything."

The old man shook his head slowly. "I've already said too much. But I'll give you this warning: if you keep digging, you'll find what your sister found. And look what happened to her."

He turned to leave, but Elena called after him. "Wait! At least tell me your name."

He paused at the door, rain beginning to soak his coat. "I'm the one who found her," he said quietly. "And I've regretted it every day since."

Then he was gone, leaving Elena alone with the storm and her questions.`;

  const handleSaveEvaluation = () => {
    console.log('Saving evaluation:', {
      manuscriptId,
      ratings: {
        overall: overallRating,
        premise: premiseRating,
        writing: writingRating,
        characters: charactersRating,
        pace: paceRating,
        marketability: marketabilityRating,
      },
      privateNotes,
      strengths,
      weaknesses,
      editorialNotes,
      marketComps,
      targetAudience,
    });
    window.alert('Evaluation saved successfully!');
    setShowEvaluation(false);
  };

  const handleSubmitDecision = () => {
    if (!decision) {
      window.alert('Please select a decision');
      return;
    }
    if (!feedbackDraft.trim()) {
      window.alert('Please write feedback for the author');
      return;
    }

    console.log('Submitting decision:', {
      manuscriptId,
      decision,
      feedback: feedbackDraft,
    });

    window.alert(`Decision submitted: ${decision.replace('_', ' ').toUpperCase()}`);
    window.location.href = '/agent/manuscripts';
  };

  const RatingStars = ({
    value,
    onChange,
    label
  }: {
    value: number;
    onChange: (value: number) => void;
    label: string;
  }) => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: theme.colors.textPrimary,
        marginBottom: '8px',
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => onChange(star)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '28px',
              padding: 0,
              color: star <= value ? theme.colors.warning : theme.colors.border,
              transition: 'color 0.2s',
            }}
          >
            ★
          </button>
        ))}
        <span style={{
          marginLeft: '12px',
          fontSize: '14px',
          color: theme.colors.textSecondary,
          lineHeight: '28px',
        }}>
          {value > 0 ? `${value}/5` : 'Not rated'}
        </span>
      </div>
    </div>
  );

  if (showDecision) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Make Decision
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Decide on "{manuscript.title}" by {manuscript.author}
            </p>
          </div>

          {/* Decision Options */}
          <Card style={{ marginBottom: '24px' }}>
            <label style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '16px',
              display: 'block',
            }}>
              Your Decision *
            </label>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              <button
                onClick={() => setDecision('offer')}
                style={{
                  padding: '20px',
                  border: `2px solid ${decision === 'offer' ? theme.colors.success : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: decision === 'offer' ? theme.colors.successLight : theme.colors.white,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  ✓ Offer Representation
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                }}>
                  I want to represent this manuscript and offer the author representation
                </div>
              </button>

              <button
                onClick={() => setDecision('revise_resubmit')}
                style={{
                  padding: '20px',
                  border: `2px solid ${decision === 'revise_resubmit' ? theme.colors.warning : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: decision === 'revise_resubmit' ? theme.colors.warningLight : theme.colors.white,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  ↻ Revise & Resubmit
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                }}>
                  I see potential but would like to see revisions before making a decision
                </div>
              </button>

              <button
                onClick={() => setDecision('pass')}
                style={{
                  padding: '20px',
                  border: `2px solid ${decision === 'pass' ? theme.colors.error : theme.colors.border}`,
                  borderRadius: theme.borderRadius.medium,
                  backgroundColor: decision === 'pass' ? theme.colors.errorLight : theme.colors.white,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: theme.colors.textPrimary,
                  marginBottom: '4px',
                }}>
                  × Pass
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                }}>
                  This manuscript is not the right fit for my list
                </div>
              </button>
            </div>
          </Card>

          {/* Feedback Draft */}
          <Card style={{ marginBottom: '32px' }}>
            <Textarea
              label="Feedback for Author *"
              placeholder="Write your feedback to the author..."
              value={feedbackDraft}
              onChange={(e) => setFeedbackDraft(e.target.value)}
              rows={12}
              required
              helperText={
                decision === 'offer'
                  ? 'Express your enthusiasm and outline next steps'
                  : decision === 'revise_resubmit'
                  ? 'Provide constructive feedback on what revisions you\'d like to see'
                  : 'Be professional and encouraging, even when declining'
              }
            />
          </Card>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
          }}>
            <Button
              variant="ghost"
              onClick={() => setShowDecision(false)}
            >
              ← Back to Reading
            </Button>
            <Button
              onClick={handleSubmitDecision}
              disabled={!decision || !feedbackDraft.trim()}
            >
              Submit Decision
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (showEvaluation) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.primary,
      }}>
        <Header />
        <main style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '60px 40px',
        }}>
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Manuscript Evaluation
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Evaluate "{manuscript.title}" by {manuscript.author}
            </p>
          </div>

          {/* Ratings */}
          <Card style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '20px',
            }}>
              Ratings
            </h3>

            <RatingStars
              label="Overall Rating"
              value={overallRating}
              onChange={setOverallRating}
            />
            <RatingStars
              label="Premise/Concept"
              value={premiseRating}
              onChange={setPremiseRating}
            />
            <RatingStars
              label="Writing Quality"
              value={writingRating}
              onChange={setWritingRating}
            />
            <RatingStars
              label="Character Development"
              value={charactersRating}
              onChange={setCharactersRating}
            />
            <RatingStars
              label="Pacing"
              value={paceRating}
              onChange={setPaceRating}
            />
            <RatingStars
              label="Market Potential"
              value={marketabilityRating}
              onChange={setMarketabilityRating}
            />
          </Card>

          {/* Strengths & Weaknesses */}
          <Card style={{ marginBottom: '24px' }}>
            <Textarea
              label="Strengths"
              placeholder="What works well in this manuscript?"
              value={strengths}
              onChange={(e) => setStrengths(e.target.value)}
              rows={4}
            />
          </Card>

          <Card style={{ marginBottom: '24px' }}>
            <Textarea
              label="Weaknesses"
              placeholder="What needs improvement?"
              value={weaknesses}
              onChange={(e) => setWeaknesses(e.target.value)}
              rows={4}
            />
          </Card>

          {/* Editorial Notes */}
          <Card style={{ marginBottom: '24px' }}>
            <Textarea
              label="Editorial Notes"
              placeholder="Specific editorial feedback and suggestions..."
              value={editorialNotes}
              onChange={(e) => setEditorialNotes(e.target.value)}
              rows={6}
            />
          </Card>

          {/* Market Analysis */}
          <Card style={{ marginBottom: '24px' }}>
            <Textarea
              label="Market Comparisons"
              placeholder="Comparable titles and market positioning..."
              value={marketComps}
              onChange={(e) => setMarketComps(e.target.value)}
              rows={4}
            />
          </Card>

          <Card style={{ marginBottom: '24px' }}>
            <Textarea
              label="Target Audience"
              placeholder="Who is the ideal reader for this book?"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              rows={3}
            />
          </Card>

          {/* Private Notes */}
          <Card style={{ marginBottom: '32px' }}>
            <Textarea
              label="Private Notes (For Your Records Only)"
              placeholder="Personal notes, concerns, follow-up items..."
              value={privateNotes}
              onChange={(e) => setPrivateNotes(e.target.value)}
              rows={4}
              helperText="These notes are private and will not be shared with the author"
            />
          </Card>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
          }}>
            <Button
              variant="ghost"
              onClick={() => setShowEvaluation(false)}
            >
              ← Back to Reading
            </Button>
            <Button onClick={handleSaveEvaluation}>
              Save Evaluation
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      {/* Sticky Control Bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: theme.colors.white,
        borderBottom: `1px solid ${theme.colors.border}`,
        padding: '16px 40px',
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              margin: '0 0 4px 0',
            }}>
              {manuscript.title}
            </h2>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              margin: 0,
            }}>
              by {manuscript.author}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Button
              variant="outline"
              size="small"
              onClick={() => setShowEvaluation(true)}
            >
              Evaluate
            </Button>
            <Button
              size="small"
              onClick={() => setShowDecision(true)}
            >
              Make Decision
            </Button>
          </div>
        </div>
      </div>

      <main style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px',
      }}>
        {/* Chapter Navigation */}
        <Card style={{ marginBottom: '24px', backgroundColor: theme.colors.backgroundAlt }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Button
              variant="ghost"
              size="small"
              onClick={() => setCurrentChapter(Math.max(1, currentChapter - 1))}
              disabled={currentChapter === 1}
            >
              ← Previous
            </Button>

            <Select
              label=""
              value={currentChapter.toString()}
              onChange={(e) => setCurrentChapter(parseInt(e.target.value))}
              options={chapters.map(ch => ({
                value: ch.number.toString(),
                label: `Chapter ${ch.number}: ${ch.title}`,
              }))}
            />

            <Button
              variant="ghost"
              size="small"
              onClick={() => setCurrentChapter(Math.min(chapters.length, currentChapter + 1))}
              disabled={currentChapter === chapters.length}
            >
              Next →
            </Button>
          </div>
        </Card>

        {/* Manuscript Content */}
        <Card>
          <div style={{
            marginBottom: '24px',
            paddingBottom: '24px',
            borderBottom: `1px solid ${theme.colors.border}`,
          }}>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '400',
              color: theme.colors.textPrimary,
              marginBottom: '8px',
            }}>
              Chapter {currentChapter}: {chapters[currentChapter - 1].title}
            </h1>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              {chapters[currentChapter - 1].wordCount.toLocaleString()} words
            </p>
          </div>

          <div style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: theme.colors.textPrimary,
            whiteSpace: 'pre-wrap',
            fontFamily: theme.fonts.primary,
          }}>
            {sampleContent}
          </div>
        </Card>

        {/* Chapter Navigation Bottom */}
        <div style={{
          marginTop: '40px',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
          <Button
            variant="outline"
            onClick={() => setCurrentChapter(Math.max(1, currentChapter - 1))}
            disabled={currentChapter === 1}
          >
            ← Previous Chapter
          </Button>

          <Button
            variant="outline"
            onClick={() => setCurrentChapter(Math.min(chapters.length, currentChapter + 1))}
            disabled={currentChapter === chapters.length}
          >
            Next Chapter →
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};
